import {
    Alert,
    MenuItem,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import React, {useCallback, useMemo, useState} from 'react';
import NextButton from "../../components/NextButton/NextButton";
import Page from "../../components/Page/Page";
import {useFirestore, useFirestoreModule} from "../../data/FirebaseContext";
import {useUid} from "../../data/SignInContext";

const ALLOWED_UID = "62x3YwsuIbTtYnEblqnhz1BIddJ3";

const products = [
    { id: "vl", label: "vl", price: 79000 },
    { id: "vlf", label: "vlf", price: 99000 },
    { id: "vl2", label: "vl2", price: 158000 },
    { id: "belop", label: "belop", price: 0 }
];

const getTodayString = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
};

const createInitialForm = () => ({
    code: "",
    purchaseDate: getTodayString(),
    productId: "",
    amount: ""
});

const getProductById = productId => {
    return products.find(product => product.id === productId) || null;
};

const createValidationErrors = form => {
    const errors = {};
    const trimmedCode = form.code.trim();
    const selectedProduct = getProductById(form.productId);

    if (!trimmedCode){
        errors.code = "Skriv inn en kode.";
    } else if (!/^\d+$/.test(trimmedCode)){
        errors.code = "Koden kan kun inneholde tall.";
    }

    if (!form.purchaseDate){
        errors.purchaseDate = "Velg kjopsdato.";
    }

    if (!selectedProduct){
        errors.productId = "Velg et produkt.";
    }

    if (!form.amount){
        errors.amount = "Skriv inn et belop.";
    } else if (!/^\d+$/.test(form.amount)){
        errors.amount = "Belopet ma vaere et helt tall.";
    } else if (parseInt(form.amount, 10) <= 0){
        errors.amount = "Belopet ma vaere storre enn 0.";
    }

    if (selectedProduct && selectedProduct.id !== "belop"){
        const expectedAmount = (selectedProduct.price / 100).toString();
        if (form.amount !== expectedAmount){
            errors.amount = "Belopet stemmer ikke med valgt produkt.";
        }
    }

    return errors;
};

const getPurchaseTimestamp = purchaseDate => {
    return Math.floor(new Date(`${purchaseDate}T12:00:00`).getTime() / 1000);
};

const CustomCardView = () => {
    const db = useFirestore();
    const firestore = useFirestoreModule();
    const uid = useUid();
    const [form, setForm] = useState(createInitialForm);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [success, setSuccess] = useState(null);

    const selectedProduct = useMemo(() => {
        return getProductById(form.productId);
    }, [form.productId]);

    const amountDisabled = selectedProduct && selectedProduct.id !== "belop";

    const updateField = useCallback((field, value) => {
        setForm(prevForm => ({
            ...prevForm,
            [field]: value
        }));
        setErrors(prevErrors => ({
            ...prevErrors,
            [field]: null
        }));
        setSubmitError(null);
    }, []);

    const updateCode = useCallback(event => {
        updateField('code', event.target.value);
    }, [updateField]);

    const updatePurchaseDate = useCallback(event => {
        updateField('purchaseDate', event.target.value);
    }, [updateField]);

    const updateProduct = useCallback(event => {
        const productId = event.target.value;
        const product = getProductById(productId);

        setForm(prevForm => ({
            ...prevForm,
            productId: productId,
            amount: product ? (product.price / 100).toString() : ""
        }));
        setErrors(prevErrors => ({
            ...prevErrors,
            productId: null,
            amount: null
        }));
        setSubmitError(null);
    }, []);

    const updateAmount = useCallback(event => {
        updateField('amount', event.target.value);
    }, [updateField]);

    const handleSubmit = useCallback(async () => {
        if (!firestore || !db || submitting) return;

        const nextErrors = createValidationErrors(form);
        if (Object.keys(nextErrors).length > 0){
            setErrors(nextErrors);
            return;
        }

        const trimmedCode = form.code.trim();
        const product = getProductById(form.productId);
        const amount = parseInt(form.amount, 10) * 100;
        const purchaseConfirmationTs = getPurchaseTimestamp(form.purchaseDate);

        setSubmitting(true);
        setSubmitError(null);

        try {
            const giftcardRef = firestore.doc(db, 'giftcards', trimmedCode);
            const existingGiftcard = await firestore.getDoc(giftcardRef);
            if (existingGiftcard.exists()){
                setErrors(prevErrors => ({
                    ...prevErrors,
                    code: "Denne koden finnes allerede i giftcards."
                }));
                setSubmitting(false);
                return;
            }

            const sessionQuery = firestore.query(
                firestore.collection(db, 'sessions'),
                firestore.where('code', '==', trimmedCode)
            );
            const existingSessions = await firestore.getDocs(sessionQuery);
            if (!existingSessions.empty){
                setErrors(prevErrors => ({
                    ...prevErrors,
                    code: "Denne koden finnes allerede i sessions."
                }));
                setSubmitting(false);
                return;
            }

            const giftcardData = {
                code: trimmedCode,
                custom: true,
                venue: 'custom',
                productId: product.id,
                purchaseDate: form.purchaseDate,
                purchaseConfirmed: true,
                purchaseConfirmationTs: purchaseConfirmationTs,
                sent: false,
                bringLabelUrl: null,
                withdrawals: [],
                reservationRequestData: {
                    code: trimmedCode,
                    custom: true,
                    venue: 'custom',
                    productId: product.id,
                    price: amount,
                    purchaseDate: form.purchaseDate
                }
            };

            const sessionRef = firestore.doc(firestore.collection(db, 'sessions'));
            const sessionData = {
                code: trimmedCode,
                custom: true,
                venue: 'custom',
                productId: product.id,
                purchaseDate: form.purchaseDate,
                createdAt: purchaseConfirmationTs,
                reservationRequestData: {
                    code: trimmedCode,
                    custom: true,
                    venue: 'custom',
                    productId: product.id,
                    price: amount,
                    purchaseDate: form.purchaseDate
                },
                customerData: {
                    name: 'admin',
                    email: 'admin',
                    address: {
                        line1: 'admin',
                        line2: '',
                        city: 'admin',
                        postal_code: '0000'
                    }
                }
            };

            const batch = firestore.writeBatch(db);
            batch.set(giftcardRef, giftcardData);
            batch.set(sessionRef, sessionData);
            await batch.commit();

            setSuccess({
                code: trimmedCode,
                productId: product.id,
                amount: amount / 100,
                purchaseDate: form.purchaseDate
            });
            setForm(createInitialForm());
            setErrors({});
        } catch (err){
            console.error(err);
            setSubmitError('Kunne ikke opprette gavekortet. Kontroller tilgang og prov igjen.');
        }

        setSubmitting(false);
    }, [db, firestore, form, submitting]);

    if (uid !== ALLOWED_UID){
        return (
            <Page>
                <Typography variant={'h4'} textAlign={'center'} gutterBottom>Ingen tilgang</Typography>
                <Typography textAlign={'center'}>Denne siden er kun tilgjengelig for admin-brukeren.</Typography>
            </Page>
        );
    }

    return(
        <Page>
            <Typography variant={'h4'} gutterBottom textAlign={'center'}>Opprett gavekort manuelt</Typography>
            <Typography textAlign={'center'} gutterBottom>
                Lager et bekreftet gavekort direkte i Firestore med venue satt til custom.
            </Typography>
            <Paper elevation={3} style={{padding: '1.5rem', marginTop: '1.5rem'}}>
                <div style={{display: 'grid', gap: '1rem'}}>
                    <TextField
                        fullWidth
                        label={'Kode'}
                        value={form.code}
                        onChange={updateCode}
                        error={Boolean(errors.code)}
                        helperText={errors.code || 'Kun tall. Ledende nuller beholdes.'}
                        inputProps={{inputMode: 'numeric'}}
                    />
                    <TextField
                        fullWidth
                        label={'Kjopsdato'}
                        type={'date'}
                        value={form.purchaseDate}
                        onChange={updatePurchaseDate}
                        error={Boolean(errors.purchaseDate)}
                        helperText={errors.purchaseDate || 'Lagres som valgt dato kl. 12:00.'}
                        InputLabelProps={{shrink: true}}
                    />
                    <TextField
                        fullWidth
                        select
                        label={'Produkt'}
                        value={form.productId}
                        onChange={updateProduct}
                        error={Boolean(errors.productId)}
                        helperText={errors.productId || 'Velg hvilket produkt gavekortet gjelder.'}
                    >
                        {products.map(product => {
                            return (
                                <MenuItem key={product.id} value={product.id}>
                                    {product.label}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                    <TextField
                        fullWidth
                        label={'Belop'}
                        value={form.amount}
                        onChange={updateAmount}
                        error={Boolean(errors.amount)}
                        helperText={errors.amount || (amountDisabled ? 'Auto-utfylt for fastprisprodukt.' : 'Skriv inn helt belop i kroner.')}
                        disabled={Boolean(amountDisabled)}
                        inputProps={{inputMode: 'numeric'}}
                    />
                </div>
            </Paper>
            {submitError ? (
                <Alert severity={'error'} style={{marginTop: '1rem'}}>
                    {submitError}
                </Alert>
            ) : null}
            {success ? (
                <Alert severity={'success'} style={{marginTop: '1rem'}}>
                    Opprettet gavekort {success.code} - {success.productId} - {success.amount},- - {success.purchaseDate}
                </Alert>
            ) : null}
            <NextButton onClick={handleSubmit} disabled={submitting || !firestore || !db}>
                {submitting ? 'Oppretter...' : 'Create gift card'}
            </NextButton>
        </Page>
    )
};

export default CustomCardView;
